'use client';
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useState } from "react";
import YouTube from "react-youtube";
import { cn } from "../../common/helpers.js";
import { Typography } from "../Typography/index.js";
const getYoutubeVideoIdFromUrl = (urlString)=>{
    const url = new URL(urlString);
    var _url_searchParams_get;
    if (url.searchParams.has('v')) return (_url_searchParams_get = url.searchParams.get('v')) !== null && _url_searchParams_get !== void 0 ? _url_searchParams_get : null;
    if (url.pathname.includes('/embed/')) return url.pathname.split('/').pop();
    return null;
};
const urlSniffer = (url)=>{
    const MATCH_URL_YOUTUBE = /(?:youtu\.be\/|youtube(?:-nocookie|education)?\.com\/(?:embed\/|v\/|watch\/|watch\?v=|watch\?.+&v=|shorts\/|live\/))((\w|-){11})|youtube\.com\/playlist\?list=|youtube\.com\/user\//;
    if (MATCH_URL_YOUTUBE.test(url)) return 'youtube';
    return null;
};
const extractStartTime = (url)=>{
    const parsedUrl = new URL(url);
    const timeParam = parsedUrl.searchParams.get('t');
    if (timeParam) {
        const timeInSeconds = Number.parseInt(timeParam, 10);
        return Number.isNaN(timeInSeconds) ? undefined : timeInSeconds;
    }
    return undefined;
};
export const getVideoIdFromUrl = (url)=>{
    try {
        const strat = urlSniffer(url);
        if (strat === 'youtube') return getYoutubeVideoIdFromUrl(url);
        return null;
    } catch (_) {
        return null;
    }
};
/**
 * Video Player - [API](https://scl.sharp.com/?path=/docs/components-videoplayer) | [DSM](https://sharphealthcare.notion.site/Video-player-7002024413b34ab7b590309ad7061bcd)
 *
 * @example
 *
 * <VideoPlayer
 *  url='https://youtube.com/sharphealthcare'
 *  heading="Sharp healthcare component library"
 *  description="Cool video" />
 *
 *  <VideoPlayer
 *  url='https://youtube.com/sharphealthcare&t=30'
 *  heading="Sharp healthcare component library"
 *  description="Cool video starting at 30 seconds playback" />
 *
 * <VideoPlayer
 *  url='https://youtube.com/sharphealthcare'
 *  description={<>JSX captions <strong>work</strong></>}
 *  slots={{ media: MyVideoPlayer }} />
 *
 * <VideoPlayer
 *  url='https://youtube.com/sharphealthcare'
 *  iframeId="videoId"
 *  mute={true}
 *  description="Example using onReady"
 *  onReady={(e) => {
 *    if (e.event.target) {
 *      startYouTubeTracking({
 *        id: 'videoId',
 *        video: 'videoId', // matches iframeId above
 *        captureEvents: ['play', 'pause', 'end', 'percent_progress', 'error',
 *      })
 *    } else {
 *      console.error('Cannot find YouTube iframe to track analytics')
 *    }
 *  }}
 * />
 */ const VideoPlayer = /*#__PURE__*/ forwardRef((props, ref)=>{
    var _slotProps_heading, _slotProps_description;
    const { as: Root = 'figure', autoPlay, loop, url, heading, description, presentation = false, mute = false, loading = 'lazy', iframeId, onReady, slots = {}, slotProps = {} } = props, rest = _object_without_properties(props, [
        "as",
        "autoPlay",
        "loop",
        "url",
        "heading",
        "description",
        "presentation",
        "mute",
        "loading",
        "iframeId",
        "onReady",
        "slots",
        "slotProps"
    ]);
    const [isPresentation, setIsPresentation] = useState(presentation);
    var _getVideoIdFromUrl;
    const videoId = (_getVideoIdFromUrl = getVideoIdFromUrl(url)) !== null && _getVideoIdFromUrl !== void 0 ? _getVideoIdFromUrl : '';
    var _slots_heading;
    const Heading = (_slots_heading = slots.heading) !== null && _slots_heading !== void 0 ? _slots_heading : Typography;
    var _slotProps_heading1;
    const _ref = (_slotProps_heading1 = slotProps === null || slotProps === void 0 ? void 0 : slotProps.heading) !== null && _slotProps_heading1 !== void 0 ? _slotProps_heading1 : {}, { className: headingSlotPropsClassName } = _ref, headingSlotProps = _object_without_properties(_ref, [
        "className"
    ]);
    const headingDefaultProps = {
        variant: 'h3'
    };
    const headingProps = slots.heading ? headingSlotProps : _object_spread({}, headingDefaultProps, headingSlotProps);
    var _slots_description;
    const Description = (_slots_description = slots === null || slots === void 0 ? void 0 : slots.description) !== null && _slots_description !== void 0 ? _slots_description : Typography;
    var _slotProps_description1;
    const _ref1 = (_slotProps_description1 = slotProps === null || slotProps === void 0 ? void 0 : slotProps.description) !== null && _slotProps_description1 !== void 0 ? _slotProps_description1 : {}, { className: descriptionSlotPropsClassName } = _ref1, descriptionSlotProps = _object_without_properties(_ref1, [
        "className"
    ]);
    const descriptionDefaultProps = {
        as: 'figcaption',
        variant: 'small-body',
        align: 'left'
    };
    const descriptionProps = (slots === null || slots === void 0 ? void 0 : slots.description) ? descriptionSlotProps : _object_spread({}, descriptionDefaultProps, descriptionSlotProps);
    const escFunction = useCallback((event)=>{
        if (presentation) {
            if (event.key === 'Escape') {
                setIsPresentation(false);
            } else if (event.key === ' ') {
                setIsPresentation(true);
            }
        }
    }, [
        presentation
    ]);
    useEffect(()=>{
        document.addEventListener('keydown', escFunction, true);
        return ()=>{
            document.removeEventListener('keydown', escFunction, true);
        };
    }, [
        escFunction
    ]);
    return /*#__PURE__*/ _jsxs(Root, _object_spread_props(_object_spread({
        ref: ref
    }, rest), {
        children: [
            /*#__PURE__*/ _jsx(YouTube, {
                videoId: videoId,
                title: heading,
                loading: loading,
                id: iframeId,
                opts: {
                    playerVars: {
                        // https://developers.google.com/youtube/player_parameters
                        autoplay: autoPlay ? 1 : 0,
                        controls: presentation ? 0 : 1,
                        loop: loop ? 1 : 0,
                        playlist: loop ? videoId : undefined,
                        rel: 0,
                        start: extractStartTime(url)
                    }
                },
                className: "w-full max-w-full relative h-0 pb-[56.25%]",
                iframeClassName: cn('w-full max-w-full h-full inset-0 rounded-xl', !isPresentation && 'absolute', isPresentation && 'fixed z-[9999]'),
                onReady: (e)=>{
                    if (mute || autoPlay) {
                        e.target.mute();
                    }
                    onReady === null || onReady === void 0 ? void 0 : onReady({
                        event: e,
                        type: 'youtube'
                    });
                }
            }),
            (heading !== null && heading !== void 0 ? heading : slotProps === null || slotProps === void 0 ? void 0 : (_slotProps_heading = slotProps.heading) === null || _slotProps_heading === void 0 ? void 0 : _slotProps_heading.children) && /*#__PURE__*/ _jsx(Heading, _object_spread_props(_object_spread({
                className: cn('mt-5', headingSlotPropsClassName)
            }, headingProps), {
                children: heading
            })),
            (description !== null && description !== void 0 ? description : slotProps === null || slotProps === void 0 ? void 0 : (_slotProps_description = slotProps.description) === null || _slotProps_description === void 0 ? void 0 : _slotProps_description.children) && /*#__PURE__*/ _jsx(Description, _object_spread_props(_object_spread({
                className: cn('mt-2 empty:hidden w-0 min-w-full scl-rich-text', descriptionSlotPropsClassName)
            }, descriptionProps), {
                children: description
            }))
        ]
    }));
});
export { VideoPlayer };
